import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { GetCompanyFilter } from '../services/AttendanceReportFilters/GetCompanyFilter';
import { GetEmployees } from '../services/Employees/GetEmployees';
import { ReportTypesEnum } from '../enums/reportTypes';
import { GetReportsList } from '../services/Reports/GetReportsList';
import { refDebounced } from '@vueuse/shared';

export const useAttendanceReportStore = defineStore('attendanceReport', () => {
  const isSummaryReportActive = ref(true);
  const isAccessLevelOn = ref(true);
  const summaryReportList = ref([]);
  const summaryReportPagination = ref({ currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 10 });
  const isSummaryReportInitialState = ref(true);
  const isSummaryReportLoading = ref(false);
  const dateFrom = ref('');
  const dateTo = ref('');
  const companyList = ref([]);
  const selectedCompany = ref({ id: 0, name: '' });
  const selectedCompanyId = ref('');
  const selectedCompanyName = ref('');
  const getCompanyList = async () => {
    const result = await GetCompanyFilter();
    selectedCompanyId.value = result.defaultCompanyId;
    selectedCompanyName.value = result.defaultCompanyName;
    processCompanyDropdownList(result.paginatedCompanyFilterListDto.data, result.defaultCompanyId);
  };

  const processCompanyDropdownList = (compList, defaultCompanyId) => {
    const optionsList = compList
      .map((val) => {
        return {
          ...val,
          text: val.companyName,
          value: val.id,
          selected: val.id === defaultCompanyId,
        };
      })
      .sort((a, b) => a.companyName.localeCompare(b.companyName));
    const result = [];
    let currentFirstLetter = '';
    let currentGroup = [];
    optionsList.forEach((item) => {
      const firstLetter = item.companyName.charAt(0).toUpperCase();

      if (firstLetter !== currentFirstLetter) {
        if (currentGroup.length > 0) {
          result.push({ text: currentFirstLetter, optgroup: true, disabled: true });
          result.push(...currentGroup);
        }
        currentFirstLetter = firstLetter;
        currentGroup = [];
      }

      currentGroup.push(item);
    });
    if (currentGroup.length > 0) {
      result.push({ text: currentFirstLetter, optgroup: true, disabled: true });
      result.push(...currentGroup);
    }
    companyList.value = sortOptionList(result);
  };
  const sortOptionList = (combineList) => {
    return combineList.sort(function (a, b) {
      if (a.text < b.text) {
        return -1;
      }
      if (a.text > b.text) {
        return 1;
      }
      return 0;
    });
  };

  const isFirstLoadEmpty = ref(false);
  watch(selectedCompanyId, async (newVal) => {
    const getCompany = companyList.value.find((val) => val.id === newVal);
    selectedCompanyName.value = getCompany.companyName;
    resetReportEmployee();
    await getEmployees();
    if (reportEmployeeList.value.length === 0) {
      isFirstLoadEmpty.value = true;
    } else {
      isFirstLoadEmpty.value = false;
    }
  });

  const reportEmployeeList = ref([]);
  const selectedEmployees = ref([]);
  const selectedEmployeesText = computed(() => {
    if (selectedCompanyName.value === '') {
      return 'Loading';
    }
    if (isFirstLoadEmpty.value) {
      return 'No Employee Found';
    }
    if (selectedEmployees.value.length === 0) {
      return 'All';
    }
    return `${selectedEmployees.value.length} employee${selectedEmployees.value.length > 1 ? 's' : ''} selected`;
  });
  const searchEmployee = ref('');
  const debouncedSearchEmployee = refDebounced(searchEmployee, 500);
  watch(debouncedSearchEmployee, async (newVal) => {
    reportEmployeeListLoader.value = true;
    reportEmployeeList.value = [];
    if (newVal.length != 0) {
      reportEmployeeListCurrentPage.value = 1;
      const params = { Fullname: newVal, PageNumber: 1 };
      await getEmployees(params);
    } else {
      await getEmployees();
    }
  });
  const reportEmployeeListLoader = ref(false);
  const reportEmployeeListCurrentPage = ref(1);
  const reportEmployeeListTotalPages = ref(0);
  const getEmployees = async (params) => {
    reportEmployeeListLoader.value = true;
    await GetEmployees({
      CompanyId: selectedCompanyId.value,
      ...(params ? { ...params } : {}),
    }).then((result) => {
      const mapEmployeeBasicInfo = result.data.map((d) => d.basicInformation);
      reportEmployeeList.value = reportEmployeeList.value.concat(mapEmployeeBasicInfo);
      reportEmployeeListTotalPages.value = result.pagination.totalPages;
      reportEmployeeListLoader.value = false;
    });
  };
  const resetReportEmployee = () => {
    searchEmployee.value = '';
    selectedEmployees.value = [];
    reportEmployeeList.value = [];
    reportEmployeeListCurrentPage.value = 1;
    reportEmployeeListTotalPages.value = 0;
  };
  // Exported Files
  const exportedReports = ref([]);
  const exportedReportsPagination = ref({ currentPage: 1, totalPages: 1, totalItems: 0, itemsPerPage: 10 });
  const isExportedReportsLoading = ref(false);
  const isExportedReportsInitialState = ref(true);
  const getExportedAttendanceReport = async ({ PageNumber, RowsPerPage }) => {
    isExportedReportsLoading.value = true;
    try {
      const response = await GetReportsList({
        PageNumber,
        RowsPerPage,
        ReportType: ReportTypesEnum.attendanceReport,
      });
      if (response.errors) {
        exportedReports.value = [];
        return;
      }
      exportedReports.value = response.data;
      exportedReportsPagination.value = response.pagination;
    } finally {
      isExportedReportsInitialState.value = false;
      isExportedReportsLoading.value = false;
    }
  };

  return {
    isSummaryReportActive,
    isAccessLevelOn,
    summaryReportList,
    summaryReportPagination,
    isSummaryReportInitialState,
    isSummaryReportLoading,
    dateFrom,
    dateTo,
    companyList,
    getCompanyList,
    selectedCompany,
    selectedCompanyId,
    selectedCompanyName,
    selectedEmployees,
    selectedEmployeesText,
    searchEmployee,
    reportEmployeeList,
    reportEmployeeListLoader,
    getEmployees,
    reportEmployeeListCurrentPage,
    reportEmployeeListTotalPages,
    isFirstLoadEmpty,
    exportedReports,
    exportedReportsPagination,
    isExportedReportsLoading,
    isExportedReportsInitialState,
    getExportedAttendanceReport,
  };
});
